import {Component, OnInit} from '@angular/core';
import {ComponentHelper, FormBaseComponent, ValidationPattern, ValidatorService} from '@utils-lib';
import {
    AppNameEnum,
    AuswahlEnumLabels,
    SollIstVersteuerungEnumLabels,
    SteuerIdAntragGrundEnumLabels,
    UmsatzsteuerAngaben
} from '@nachdigal';
import {FormBuilder} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {DatevMessageService} from '@datev/ngx-ui-components';
import {ActivatedRoute} from '@angular/router';

@Component({
    selector: 'common-ui-anmeldung-abfuehrung-ust',
    templateUrl: './anmeldung-abfuehrung-ust.component.html',
    styleUrls: ['./anmeldung-abfuehrung-ust.component.scss']
})
export class AnmeldungAbfuehrungUstComponent extends FormBaseComponent<UmsatzsteuerAngaben> implements OnInit {

    public sollIstVersteuerungEnum = SollIstVersteuerungEnumLabels;
    public steuerIdAntragGrundEnum = SteuerIdAntragGrundEnumLabels;
    public auswahlEnum = AuswahlEnumLabels;
    public isKapG = false;
    public isKapGAus = false;
    public isPersG = false;
    public activateBisherigeVerhaeltnisse = true;

    constructor(private readonly formBuilder: FormBuilder,
                private readonly validatorService: ValidatorService,
                private readonly translateService: TranslateService,
                protected readonly messageService: DatevMessageService,
                protected readonly activatedRoute: ActivatedRoute) {
        super(messageService);
    }

    public ngOnInit(): void {
        super.ngOnInit();
        const appEnum = ComponentHelper.getAppEnum(this.activatedRoute.snapshot);
        if (appEnum === AppNameEnum.FragebogenPersG) {
            this.isPersG = true;
            this.activateBisherigeVerhaeltnisse = false;
        }
        if (appEnum === AppNameEnum.FragebogenKapG) {
            this.isKapG = true;
            this.activateBisherigeVerhaeltnisse = false;
        }
        if (appEnum === AppNameEnum.FragebogenKapGAus) {
            this.isKapGAus = true;
            this.activateBisherigeVerhaeltnisse = false;
        }
        if ((this.isKapG || this.isKapGAus) && this.data.ustIdentifikationsnummerWirdBeantragtGrund === undefined) {
            this.form.get('ustIdentifikationsnummerWirdBeantragtGrund').setValue(null);
        }
        if(this.activateBisherigeVerhaeltnisse){
          if (!this.data.bisherigeVerhaeltnisse) {
            this.data.bisherigeVerhaeltnisse = {};
          }
          this.form.addControl('bisherigeVerhaeltnisse', this.formBuilder.group({
            bereitsUmsatzsteuerlichGefuehrt: [this.data.bisherigeVerhaeltnisse.bereitsUmsatzsteuerlichGefuehrt],
            ordnungskriterium: [
              this.data.bisherigeVerhaeltnisse.ordnungskriterium,
              [
                this.validatorService.required(),
                this.validatorService.pattern(ValidationPattern.STEUERNUMMER,
                  this.translateService.instant('COMMON_UI.VALIDATION.STEUERNUMMER_INVALID_PATTERN'))
              ]
            ]
          }));
        }
    }

    public isIstVersteuerungRequired(): boolean {
        return this.data.sollIstVersteuerung === SollIstVersteuerungEnumLabels.Istversteuerung &&
            (!this.data.istversteuerungGesamtumsatz
                && !this.data.istversteuerungBefreiung
                && !this.data.istversteuerungFreierBeruf);
    }

    public isSumZahllastUeberschussRequired(): boolean {
      return this.data.auswahl === AuswahlEnumLabels._1
        || this.data.auswahl === AuswahlEnumLabels._2;
    }

    public onBenoetigtChanged(): void {
        if ((this.isKapG || this.isKapGAus) && !this.data.ustIdentifikationsnummerBenoetigt) {
            this.form.get('ustIdentifikationsnummerWirdBeantragtGrund').setValue(null);
        }
    }

    protected createForm(): void {
        const vollerPositiverGeldBetrag = 'COMMON_UI.VALIDATION.VOLLER_POSITIVER_GELDBETRAG_PATTERN';
        const sumMaxLength = 12;
        const umsatzMaxLength = 200;

        this.form = this.formBuilder.group({
            summeUmsaetzeJahrBetriebseroeffnung: [
                this.data.summeUmsaetzeJahrBetriebseroeffnung, [
                    this.validatorService.required(),
                    this.validatorService.maxLength(sumMaxLength),
                    this.validatorService.pattern(ValidationPattern.VOLLER_POSITIVER_GELDBETRAG,
                        this.translateService.instant(vollerPositiverGeldBetrag))
                ]],
            summeUmsaetzeJahrBetriebseroeffnungTest: [
                this.data.summeUmsaetzeJahrBetriebseroeffnung, [
                    this.validatorService.required(),
                    this.validatorService.maxLength(sumMaxLength),
                    this.validatorService.pattern(ValidationPattern.VOLLER_POSITIVER_GELDBETRAG,
                        this.translateService.instant(vollerPositiverGeldBetrag))
                ]],
            summeUmsaetzeFolgejahr: [
                this.data.summeUmsaetzeFolgejahr, [
                    this.validatorService.required(),
                    this.validatorService.maxLength(sumMaxLength),
                    this.validatorService.pattern(ValidationPattern.VOLLER_POSITIVER_GELDBETRAG,
                        this.translateService.instant(vollerPositiverGeldBetrag))
                ]],
            geschaeftsveraeusserungImGanzen: [
                this.data.geschaeftsveraeusserungImGanzen, []
            ],
            kleinunternehmerRegelung: [
                this.data.kleinunternehmerRegelung, []
            ],
            steuerfreieUmsaetze: [
                this.data.steuerfreieUmsaetze, []
            ],
            steuerfreieUmsaetzeNummer: [
                this.data.steuerfreieUmsaetzeNummer, [
                    this.validatorService.maxLength(3)
                ]
            ],
            steuerfreieUmsaetzeArt: [
                this.data.steuerfreieUmsaetzeArt, [
                    this.validatorService.maxLength(umsatzMaxLength),
                    this.validatorService.conditionalValidator(
                        () => !!this.data.steuerfreieUmsaetze,
                        this.validatorService.required()
                    )
                ]
            ],
            steuerermaessigteUmsaetze: [
                this.data.steuerermaessigteUmsaetze, []
            ],
            steuerermaessigteUmsaetzeNummer: [
                this.data.steuerermaessigteUmsaetzeNummer, [
                    this.validatorService.maxLength(3)
                ]
            ],
            steuerermaessigteUmsaetzeArt: [
                this.data.steuerermaessigteUmsaetzeArt, [
                    this.validatorService.maxLength(umsatzMaxLength),
                    this.validatorService.conditionalValidator(
                        () => !!this.data.steuerermaessigteUmsaetze,
                        this.validatorService.required()
                    )
                ]
            ],
            steuerermaessigteUmsaetze2: [
              this.data.steuerermaessigteUmsaetze2, []
            ],
            steuerermaessigteUmsaetzeArt2: [
              this.data.steuerermaessigteUmsaetzeArt2, [
                this.validatorService.maxLength(umsatzMaxLength),
                this.validatorService.conditionalValidator(
                  () => !!this.data.steuerermaessigteUmsaetze2,
                  this.validatorService.required()
                )
              ]
            ],
            steuerermaessigteUmsaetzeArt2UStG1: [
              this.data.steuerermaessigteUmsaetzeArt2UStG1, []
            ],
            steuerermaessigteUmsaetzeArt2UStG2: [
              this.data.steuerermaessigteUmsaetzeArt2UStG2, []
            ],
            steuerermaessigteUmsaetzeArt2UStG3: [
              this.data.steuerermaessigteUmsaetzeArt2UStG3, []
            ],
            steuerermaessigteUmsaetzeArt2UStG4: [
              this.data.steuerermaessigteUmsaetzeArt2UStG4, []
            ],
            durchschnittsbesteuerteUmsaetze: [
                this.data.durchschnittsbesteuerteUmsaetze, []
            ],
            durchschnittsbesteuerteUmsaetzeNummer: [
                this.data.durchschnittsbesteuerteUmsaetzeNummer, [
                    this.validatorService.conditionalValidator(
                        () => !!this.data.durchschnittsbesteuerteUmsaetze,
                        this.validatorService.required())
                ]
            ],
            durchschnittsbesteuerteUmsaetzeArt: [
                this.data.durchschnittsbesteuerteUmsaetzeArt, [
                    this.validatorService.maxLength(umsatzMaxLength),
                    this.validatorService.conditionalValidator(
                        () => !!this.data.durchschnittsbesteuerteUmsaetze,
                        this.validatorService.required())
                ]
            ],
            sollIstVersteuerung: [
                this.data.sollIstVersteuerung, []
            ],
            istversteuerungGesamtumsatz: [
                this.data.istversteuerungGesamtumsatz, []
            ],
            istversteuerungBefreiung: [
                this.data.istversteuerungBefreiung, []
            ],
            istversteuerungFreierBeruf: [
                this.data.istversteuerungFreierBeruf, []
            ],
            ustIdentifikationsnummerBenoetigt: [
                this.data.ustIdentifikationsnummerBenoetigt, []
            ],
            verzichtDurchschnittsbesteuerung: [
              this.data.verzichtDurchschnittsbesteuerung, []
            ],
            anspruchDurchschnittsbesteuerung: [
              this.data.anspruchDurchschnittsbesteuerung, []
            ],
            ustIdentifikationsnummerVorhanden: [
                this.data.ustIdentifikationsnummerVorhanden, []
            ],
            ustIdentifikationsnummer: [
                this.data.ustIdentifikationsnummer, [
                    this.validatorService.conditionalValidator(
                        () => !!this.data.ustIdentifikationsnummerVorhanden,
                        this.validatorService.required()
                    ),
                    this.validatorService.pattern(ValidationPattern.UST_ID,
                        this.translateService.instant('COMMON_UI.VALIDATION.USTID_PATTERN'))
                ]
            ],
            ustIdentifikationsnummerVergabedatum: [
                this.data.ustIdentifikationsnummerVergabedatum, [
                    this.validatorService.conditionalValidator(
                        () => !!this.data.ustIdentifikationsnummerVorhanden,
                        this.validatorService.required()
                    ), this.validatorService.date()
                ]
            ],
            ustIdentifikationsnummerWirdBeantragtGrund: [
                this.data.ustIdentifikationsnummerWirdBeantragtGrund, []
            ],
            nachweisSteuerschuldnerschaft: [
                this.data.nachweisSteuerschuldnerschaft, []
            ],
            nachweisSteuerschuldnerschaftBauleistungen: [
                this.data.nachweisSteuerschuldnerschaftBauleistungen, []
            ],
            nachweisSteuerschuldnerschaftGebaeudereinigungsleistungen: [
                this.data.nachweisSteuerschuldnerschaftGebaeudereinigungsleistungen, []
            ],
            artUnternehmen: [
                this.data.artUnternehmen, [
                    this.validatorService.maxLength(127)
                ]
            ],
            bezGesellshaft: [
                this.data.bezGesellshaft, [
                    this.validatorService.maxLength(127)
                ]
            ],
            geschaeftsveraeusserungWirtschaftsIdNummer: [
                this.data.geschaeftsveraeusserungWirtschaftsIdNummer, [
                    this.validatorService.pattern(ValidationPattern.WIRTSCHAFTS_ID,
                        this.translateService.instant('COMMON_UI.VALIDATION.WIRTSCHAFTS_ID_NUMMER_INVALID_PATTERN'))
                ]
            ],
            antragVoSt: [
                this.data.antragVoSt, []
            ],
            zeitraumVon: [
                this.data.zeitraumVon, [
                    this.validatorService.date()
                ]
            ],
            zeitraumBis: [
                this.data.zeitraumBis, [
                    this.validatorService.date()
                ]
            ],
            aktenzeichen: [
                this.data.aktenzeichen, [
                    this.validatorService.maxLength(100)
                ]
            ],
            eoriNummer: [
                this.data.eoriNummer, [
                    this.validatorService.maxLength(20)
                ]
            ],
            auswahl: [
                this.data.auswahl, []
            ],
            betrag: [
                this.data.betrag, [
                  this.validatorService.conditionalValidator(
                    () => !!this.isSumZahllastUeberschussRequired,
                    this.validatorService.required()
                  ),
                    this.validatorService.pattern('^\\d{1,10}$',
                        this.translateService.instant(vollerPositiverGeldBetrag))]
            ],
            voranmeldungMonatlich: [
                this.data.voranmeldungMonatlich
            ]
        });
    }
}
